/**
 * Контракты режима «Основы» (ТЗ §6, режим 2).
 *
 * Режим существует ради новичка: диагностика измеряет у него то, чего ещё
 * нет, а сплошное упражнение из незнакомых клавиш закрепит поиск глазами.
 * Клавиши открываются группами — ряд и пара одноимённых пальцев, — и
 * следующая группа ждёт, пока предыдущая не начнёт получаться.
 */

import type {
  HintLevel,
  LanguageCode,
  LayoutId,
  MasteryStat,
  MasteryUnit,
} from '@type2x/shared-contracts';
import type { FingerPair, KeyRow } from '@type2x/keyboard-layouts';

/**
 * Пара одноимённых пальцев обеих рук.
 *
 * Понятие принадлежит клавиатуре, а не режиму: оно одинаково для «Основ» и
 * для режимов цифр и символов, поэтому живёт в описании раскладок.
 */
export { FINGER_PAIRS } from '@type2x/keyboard-layouts';
export type { FingerPair } from '@type2x/keyboard-layouts';

/** Ряды, которые проходит режим. Цифровой ряд — отдельный режим (ТЗ §6). */
export const FUNDAMENTAL_ROWS = ['home', 'top', 'bottom'] as const satisfies readonly KeyRow[];

/**
 * Ряд, который проходит режим.
 *
 * Уже не любой ряд клавиатуры: цифровой и пробел «Основы» не проходят, а
 * экран берёт по ряду название. Пока тип был шире правды, он обещал экрану
 * ряды, названий для которых нет.
 */
export type FundamentalRow = (typeof FUNDAMENTAL_ROWS)[number];

/** Группа клавиш: один ряд, одна пара пальцев. */
export interface KeyGroup {
  readonly id: string;
  readonly row: FundamentalRow;
  readonly fingerPair: FingerPair;
  /** Символы группы в порядке следования клавиш слева направо. */
  readonly characters: readonly string[];
}

/** Снимок навыка, из которого считается открытость групп. */
export interface FundamentalsSnapshot {
  readonly language: LanguageCode;
  readonly layoutId: LayoutId;
  readonly units: Readonly<Record<string, MasteryUnit>>;
  readonly stats: Readonly<Record<string, MasteryStat>>;
}

/** Упражнение режима. */
export interface FundamentalsExercise {
  readonly seed: string;
  /** Элементы: сочетания открытых клавиш либо настоящие слова. */
  readonly items: readonly string[];
  /** Текст для набора: элементы, соединённые пробелами. */
  readonly text: string;
  /**
   * Вошли ли в упражнение настоящие слова.
   *
   * Как только открытых клавиш хватает на слова частотного списка, режим
   * переходит на них: долго печатать бессмысленные последовательности
   * новичку нельзя (ТЗ §6, правило под таблицей режимов).
   */
  readonly usesWords: boolean;
}

/** План занятия режима. */
export interface FundamentalsPlan {
  readonly language: LanguageCode;
  readonly layoutId: LayoutId;
  /** Группа, которую пользователь осваивает сейчас; null — открыты все. */
  readonly group: KeyGroup | null;
  /** Группы, уже пройденные по порогу освоенности. */
  readonly completedGroupIds: readonly string[];
  /** Все открытые символы: пройденные группы и текущая. */
  readonly unlockedCharacters: readonly string[];
  /** Учебные единицы открытых клавиш. */
  readonly units: readonly MasteryUnit[];
  readonly exercise: FundamentalsExercise;
  readonly hintLevel: HintLevel;
  /** Все группы пройдены: режим больше нечему учить. */
  readonly finished: boolean;
}

/**
 * Порог освоенности, открывающий следующую группу.
 *
 * Значение общее для всех режимов, открывающих клавиши группами, и живёт в
 * ядре обучения: два разных порога у «Основ» и «Цифр» означали бы, что
 * «уверенно» значит разное в соседних режимах.
 */
export { GROUP_MASTERY_THRESHOLD } from '@type2x/learning-engine';

/**
 * Нижняя граница длины занятия в элементах.
 *
 * Занятие короче не успевает стать занятием: на четырёх первых клавишах это
 * примерно минута, и меньше минуты новичок не успевает войти в движение.
 */
export const MIN_LESSON_ITEMS = 12;

/**
 * Верхняя граница длины занятия в элементах.
 *
 * Занятие ограничено временем, а не числом элементов, поэтому граница здесь —
 * предохранитель, а не мера: она удерживает упражнение в разумных размерах,
 * если оценка скорости окажется бессмысленно малой. Значение выбрано по
 * фактическим прогонам: даже при уверенных 300 мс на нажатие трёхминутное
 * занятие укладывается примерно в сотню элементов.
 */
export const MAX_LESSON_ITEMS = 120;

/** Длина сочетания клавиш, если настоящих слов ещё не набирается. */
export const CHUNK_LENGTH = 3;

/**
 * Сколько слов должно набираться открытыми клавишами, чтобы перейти на них.
 *
 * Одного-двух слов мало: упражнение из них было бы повторением одного и того
 * же, что хуже сочетаний.
 */
export const MIN_WORDS_TO_SWITCH = 8;

/** Уровень подсказок режима: новичку показывается и клавиша, и палец. */
export const FUNDAMENTALS_HINT_LEVEL: HintLevel = 'full-keyboard-and-finger';
